/**
 * MerchantsWidget — analytics-v2 merchant / page ranking (ADS-055 / CORE-960).
 *
 * Fetches from GET /analytics-v2/merchants with the currently-selected
 * window. Renders TWO independently-sortable tables in one widget body:
 *   - Top landing pages by conversion rate (GA4 pageViews joined against
 *     DB orders per landing_page).
 *   - Top merchants by conversion rate (GA4 merchant_selected event joined
 *     against DB orders per store_id).
 *
 * Both tables ship from the backend already ranked by conversion desc →
 * orders desc → nulls-last. Local sort state is layered on top so
 * operators can re-rank by any numeric column without a refetch. The
 * two tables have INDEPENDENT sort state — sorting the landings table
 * by orders leaves the merchants table's sort unchanged.
 *
 * All shell concerns (PROD badge, window selector, attribution caption,
 * loading/error triad, source-degradation banner, ramp-up caveat,
 * ErrorBoundary) live in AnalyticsV2Widget. This file owns only the
 * widget-specific body.
 *
 * Ramp-up caveat (CORE-960 decision — "ship-with-caveat, option 1"):
 * this is a brand-new service with almost no traffic today; ad campaigns
 * launch in the next few weeks; App Store / Play Store adoption of
 * CORE-1059 mobile changes catches up over months. Widget shows all
 * rows (no minimum-traffic floor) with a prominent caption explaining
 * that early-window numbers are informational only. The caveat itself
 * is passed to the shell via the `rampUpNote` prop.
 *
 * Null handling (see LandingPageRow / MerchantRow docs):
 *   - `conversionRate === null` → "—" (denominator=0; distinct from
 *     "traffic with zero orders" which is a real 0.00%).
 *   - `title / merchantName === null` → "—" (GA4 unavailable OR
 *     mobile-only merchant pre-CORE-1059 adoption).
 *   - `bounceRate / engagementTimeSecs === null` → "—" (GA4 unavailable).
 */

import { useMemo, useState } from 'react';
import { getAnalyticsMerchants } from '@/api/client';
import { formatCount, formatRatioAsPercent } from '@/utils/format';
import { compareNullable, type SortDir } from '@/utils/sort';
import { degradedSourceLabels } from '@/utils/sourceStatuses';
import { useAnalyticsV2Fetch } from '@/hooks/useAnalyticsV2Fetch';
import { useSortState } from '@/hooks/useSortState';
import { AnalyticsV2Widget } from './AnalyticsV2Widget';
import { AnalyticsV2ErrorBoundary } from './AnalyticsV2ErrorBoundary';
import type { FunnelWindow, LandingPageRow, MerchantRow } from '@/types/api';

/**
 * Engagement time is in seconds; display as whole seconds. Null when GA4
 * is unavailable OR sessions=0 (backend guards divide-by-zero).
 */
function formatEngagementSecs(value: number | null): string {
  return value === null ? '—' : `${Math.round(value)}s`;
}

/** Human-readable text for the title/merchantName display fields when null. */
function displayOrDash(value: string | null): string {
  return value === null || value === '' ? '—' : value;
}

// ────────────────────────────────────────────────────────────────────────
// Landing-pages table
// ────────────────────────────────────────────────────────────────────────

type LandingSortKey =
  | 'pageViews'
  | 'bounceRate'
  | 'engagementTimeSecs'
  | 'orders'
  | 'conversionRate';

interface LandingColumn {
  key: LandingSortKey;
  label: string;
  format: (row: LandingPageRow) => string;
  getValue: (row: LandingPageRow) => number | null;
}

const LANDING_COLUMNS: readonly LandingColumn[] = [
  { key: 'pageViews', label: 'Sessions', format: (r) => formatCount(r.pageViews), getValue: (r) => r.pageViews },
  { key: 'bounceRate', label: 'Bounce', format: (r) => formatRatioAsPercent(r.bounceRate), getValue: (r) => r.bounceRate },
  { key: 'engagementTimeSecs', label: 'Engagement', format: (r) => formatEngagementSecs(r.engagementTimeSecs), getValue: (r) => r.engagementTimeSecs },
  { key: 'orders', label: 'Orders', format: (r) => formatCount(r.orders), getValue: (r) => r.orders },
  { key: 'conversionRate', label: 'Conversion', format: (r) => formatRatioAsPercent(r.conversionRate), getValue: (r) => r.conversionRate },
];

function sortLandings(
  rows: readonly LandingPageRow[],
  key: LandingSortKey,
  dir: SortDir,
): LandingPageRow[] {
  const col = LANDING_COLUMNS.find((c) => c.key === key);
  if (col === undefined) return [...rows];
  return [...rows].sort((a, b) => {
    const c = compareNullable(col.getValue(a), col.getValue(b), dir);
    if (c !== 0) return c;
    // Tie-break by orders desc, matching the backend's secondary sort.
    return b.orders - a.orders;
  });
}

// ────────────────────────────────────────────────────────────────────────
// Merchants table
// ────────────────────────────────────────────────────────────────────────

type MerchantSortKey = 'merchantSelectedCount' | 'orders' | 'conversionRate';

interface MerchantColumn {
  key: MerchantSortKey;
  label: string;
  format: (row: MerchantRow) => string;
  getValue: (row: MerchantRow) => number | null;
}

const MERCHANT_COLUMNS: readonly MerchantColumn[] = [
  { key: 'merchantSelectedCount', label: 'Selections', format: (r) => formatCount(r.merchantSelectedCount), getValue: (r) => r.merchantSelectedCount },
  { key: 'orders', label: 'Orders', format: (r) => formatCount(r.orders), getValue: (r) => r.orders },
  { key: 'conversionRate', label: 'Conversion', format: (r) => formatRatioAsPercent(r.conversionRate), getValue: (r) => r.conversionRate },
];

function sortMerchants(
  rows: readonly MerchantRow[],
  key: MerchantSortKey,
  dir: SortDir,
): MerchantRow[] {
  const col = MERCHANT_COLUMNS.find((c) => c.key === key);
  if (col === undefined) return [...rows];
  return [...rows].sort((a, b) => {
    const c = compareNullable(col.getValue(a), col.getValue(b), dir);
    if (c !== 0) return c;
    return b.orders - a.orders;
  });
}

// ────────────────────────────────────────────────────────────────────────
// Widget
// ────────────────────────────────────────────────────────────────────────

const RAMP_UP_NOTE = (
  <>
    <strong>Early window — informational only.</strong> Ads
    haven&apos;t launched yet and mobile app updates propagate
    over weeks. Both rankings improve in accuracy as ad traffic
    arrives and CORE-1059 adoption saturates.
  </>
);

const DESCRIPTION = (
  <>
    <p>
      Two rankings for the selected window:
    </p>
    <ul>
      <li>
        <strong>Top landing pages by conversion</strong> — where the ad
        clicks land, ranked by conversion rate (orders / landings).
      </li>
      <li>
        <strong>Top merchants by conversion</strong> — buyers select a
        merchant from the storefront; this ranks merchants by conversion
        rate (orders / selections).
      </li>
    </ul>
    <p>
      Both are informational during the current ramp-up (see the note
      above). Click any numeric column header to sort.
    </p>
  </>
);

export function MerchantsWidget(): JSX.Element {
  // State lives here (not inside the Body) so the ErrorBoundary above
  // can key its resetKey on it and re-attempt render when the user
  // switches windows — same pattern as CampaignsWidget.
  const [selectedWindow, setSelectedWindow] = useState<FunnelWindow>('7d');

  return (
    <AnalyticsV2ErrorBoundary title="Merchants + Landings" resetKey={selectedWindow}>
      <MerchantsWidgetBody
        selectedWindow={selectedWindow}
        setSelectedWindow={setSelectedWindow}
      />
    </AnalyticsV2ErrorBoundary>
  );
}

interface MerchantsWidgetBodyProps {
  selectedWindow: FunnelWindow;
  setSelectedWindow: (w: FunnelWindow) => void;
}

function MerchantsWidgetBody({
  selectedWindow,
  setSelectedWindow,
}: MerchantsWidgetBodyProps): JSX.Element {
  // Two INDEPENDENT sort states — landing sort and merchant sort don't
  // affect each other. Both default to conversionRate desc, matching
  // the backend's primary sort so the widget looks the same before/after
  // the first click.
  const landingSort = useSortState<LandingSortKey>('conversionRate', 'desc');
  const merchantSort = useSortState<MerchantSortKey>('conversionRate', 'desc');

  const { data, error, isLoading } = useAnalyticsV2Fetch(
    () => getAnalyticsMerchants({ window: selectedWindow }),
    [selectedWindow],
  );

  const sortedLandings = useMemo(() => {
    if (data === null) return [];
    return sortLandings(data.landingPages, landingSort.sortKey, landingSort.sortDir);
  }, [data, landingSort.sortKey, landingSort.sortDir]);

  const sortedMerchants = useMemo(() => {
    if (data === null) return [];
    return sortMerchants(data.merchants, merchantSort.sortKey, merchantSort.sortDir);
  }, [data, merchantSort.sortKey, merchantSort.sortDir]);

  const degraded = data === null ? [] : degradedSourceLabels(data.sourceStatuses);

  return (
    <AnalyticsV2Widget
      title="Merchants + Landings"
      selectedWindow={selectedWindow}
      onWindowChange={setSelectedWindow}
      data={data}
      error={error}
      isLoading={isLoading}
      degradedSources={degraded}
      rampUpNote={RAMP_UP_NOTE}
      description={DESCRIPTION}
    >
      {data !== null && (
        <>
          <section className="merchants-widget-section">
            <h4>Top landing pages by conversion</h4>
            {sortedLandings.length === 0 ? (
              degraded.length > 0 ? (
                <p className="empty-state" role="status">
                  No landing data — some sources unavailable (see banner above).
                </p>
              ) : (
                <p className="empty-state" role="status">No landing pages for this window.</p>
              )
            ) : (
              <div className="table-wrap">
                <table className="merchants-widget-table">
                  {/* sr-only caption so screen readers can disambiguate the two
                      tables in this widget — both have Orders and Conversion
                      headers. */}
                  <caption className="sr-only">Landing pages</caption>
                  <thead>
                    <tr>
                      <th>Landing page</th>
                      <th>Title</th>
                      {LANDING_COLUMNS.map((col) => {
                        const isActive = landingSort.sortKey === col.key;
                        const arrow = isActive ? (landingSort.sortDir === 'desc' ? ' ↓' : ' ↑') : '';
                        return (
                          <th
                            key={col.key}
                            aria-sort={isActive ? (landingSort.sortDir === 'desc' ? 'descending' : 'ascending') : 'none'}
                          >
                            <button
                              type="button"
                              className="merchants-widget-sort-btn"
                              onClick={() => landingSort.handleSortClick(col.key)}
                            >
                              {col.label}
                              {arrow}
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLandings.map((row) => (
                      // path is unique per row (backend GROUP BY landing_page)
                      <tr key={row.path}>
                        <td className="merchants-widget-path">{row.path}</td>
                        <td>{displayOrDash(row.title)}</td>
                        {LANDING_COLUMNS.map((col) => (
                          <td key={col.key} className="merchants-widget-num">
                            {col.format(row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="merchants-widget-section">
            <h4>Top merchants by conversion</h4>
            {sortedMerchants.length === 0 ? (
              degraded.length > 0 ? (
                <p className="empty-state" role="status">
                  No merchant data — some sources unavailable (see banner above).
                </p>
              ) : (
                <p className="empty-state" role="status">No merchants for this window.</p>
              )
            ) : (
              <div className="table-wrap">
                <table className="merchants-widget-table">
                  <caption className="sr-only">Merchants</caption>
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      {MERCHANT_COLUMNS.map((col) => {
                        const isActive = merchantSort.sortKey === col.key;
                        const arrow = isActive ? (merchantSort.sortDir === 'desc' ? ' ↓' : ' ↑') : '';
                        return (
                          <th
                            key={col.key}
                            aria-sort={isActive ? (merchantSort.sortDir === 'desc' ? 'descending' : 'ascending') : 'none'}
                          >
                            <button
                              type="button"
                              className="merchants-widget-sort-btn"
                              onClick={() => merchantSort.handleSortClick(col.key)}
                            >
                              {col.label}
                              {arrow}
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMerchants.map((row) => (
                      // merchantId is a UUID, unique per row (backend GROUP BY store_id)
                      <tr key={row.merchantId}>
                        <td>{displayOrDash(row.merchantName)}</td>
                        {MERCHANT_COLUMNS.map((col) => (
                          <td key={col.key} className="merchants-widget-num">
                            {col.format(row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </AnalyticsV2Widget>
  );
}
